<template>
  <div v-if="packagePlans?.length" class="package-plans">
    <div
      v-for="packagePlan in packagePlans"
      :key="packagePlan.id"
      class="package-plan"
      @click.prevent="changeSelectPackagePlan(packagePlan)"
    >
      <div class="package-plan__info">
        <span v-if="packagePlan.selected" class="package-plan__name">Active Package Plan:</span>
        <span
          :class="['package-plan__value', { 'is-first': !packagePlan.selected }]"
        >
          {{ packagePlan.name }}
        </span>

        <custom-checkbox
          :disabled="isCheckboxDisabled"
          :checked="packagePlan.selected"
        />
      </div>

      <div
        v-if="packagePlan?.daily_limit"
        class="package-plan__drinks drinks-left"
      >
        <span class="drinks-left__label">Items used:</span>
        <span class="drinks-left__value">
          {{ itemsUsed(packagePlan) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import CustomCheckbox from '@/components/_shared/CustomCheckbox.vue';
import { orderService } from '@/_api/order.api';

export default {
  name: 'PackagePlans',

  components: {
    CustomCheckbox,
  },

  emits: ['selected:change'],

  props: {
    user: {
      type: [Object, null],
      required: true,
    },

    order: {
      type: [Object, null],
      required: true,
    }
  },

  data() {
    return {
      packagePlans: [],

      limitAlcoholPerDay: null,
    };
  },

  computed: {
    isCheckboxDisabled() {
      return [2, 3].includes(this.order.status);
    },
  },

  mounted() {
    this.limitAlcoholPerDay = this.$store.getters.limitAlcoholPerDay;
  },

  watch: {
    order: {
      async handler() {
        const canLoadPackagePlans = Boolean(isNaN(this.order?.id) && this.order?.payers?.length > 0 && this.order?.items?.length > 0);
        if (!canLoadPackagePlans) {
          this.packagePlans = [];
          return;
        }

        const { data } = await orderService.getOrderAvailablePackagePlans(this.order.id);
        this.packagePlans = (data?.payload?.content ?? [])?.map((plan) => {
          plan.selected = plan.package_plan_user_profile_id === this.order.package_plan_user_profile_id;

          return plan;
        });
      },

      deep: true,
      immediate: true,
    },
  },

  methods: {
    changeSelectPackagePlan(packagePlan) {
      if (this.isCheckboxDisabled) {
        return;
      }

      packagePlan.selected = !packagePlan.selected;
      const profileId = packagePlan.selected
        ? packagePlan.package_plan_user_profile_id
        : null;

      this.$emit('selected:change', profileId);
    },

    itemsUsed(packagePlan) {
      return `${packagePlan?.usage_count ?? 0}/${packagePlan?.daily_limit}`;
    }
  }
};
</script>

<style lang="less" scoped>
.package-plans {
  width: 100%;
  padding: 30px 0;
  row-gap: 8px;

  display: flex;
  flex-direction: column;
  background-color: #ffffff;
}

.package-plan {
  cursor: pointer;
  user-select: none;

  background-color: #F5F9FC;

  display: flex;
  flex-direction: row;
  align-items: center;

  width: 100%;

  padding: 8px 12px;

  .check {
    padding: 0 16px;
    margin-left: auto;
  }

  &__info {
    display: flex;
    flex-direction: row;

    font-size: 14px;
    line-height: 24px;

    letter-spacing: -0.6px;
    width: 100%;

    &:before {
      content: '';
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background-repeat: no-repeat;
      background-position: center;

      width: 24px;
      height: 24px;
      background-image: url("data:image/svg+xml,%3Csvg width='24' height='20' viewBox='0 0 24 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M4.8125 2.33334C4.05 2.33334 3.31874 2.63624 2.77957 3.17541C2.2404 3.71458 1.9375 4.44585 1.9375 5.20834V14.7917C1.9375 15.5542 2.2404 16.2854 2.77957 16.8246C3.31873 17.3638 4.05 17.6667 4.8125 17.6667H19.1875C19.95 17.6667 20.6813 17.3638 21.2204 16.8246C21.7596 16.2854 22.0625 15.5542 22.0625 14.7917V5.20834C22.0625 4.44585 21.7596 3.71458 21.2204 3.17541C20.6813 2.63624 19.95 2.33334 19.1875 2.33334H4.8125ZM1.7631 2.15895C2.57185 1.3502 3.66875 0.895844 4.8125 0.895844H19.1875C20.3312 0.895844 21.4281 1.35019 22.2369 2.15895C23.0456 2.9677 23.5 4.0646 23.5 5.20834V14.7917C23.5 15.9354 23.0456 17.0323 22.2369 17.8411C21.4281 18.6498 20.3312 19.1042 19.1875 19.1042H4.8125C3.66875 19.1042 2.57185 18.6498 1.7631 17.8411C0.954351 17.0323 0.5 15.9354 0.5 14.7917V5.20834C0.5 4.0646 0.954352 2.9677 1.7631 2.15895ZM7.86854 9.02679C7.44597 8.51343 7.0157 8.31584 6.62212 8.32282L6.60938 8.32305V8.32293C6.04906 8.32293 5.53125 8.81814 5.53125 9.40106C5.53125 9.54019 5.65175 9.80085 6.05869 10.0761C6.44566 10.3379 6.90683 10.4792 7.20833 10.4792H8.6482C8.43196 9.8733 8.16288 9.38437 7.86854 9.02679ZM7.86895 11.9167H7.20833C6.56468 11.9167 5.82794 11.6555 5.25328 11.2668C4.69858 10.8916 4.09375 10.2538 4.09375 9.40106C4.09375 8.00249 5.27555 6.88904 6.60293 6.88544C7.55787 6.87077 8.36941 7.37338 8.9784 8.11321C9.2105 8.39518 9.41947 8.71795 9.60417 9.07442C9.78886 8.71795 9.99783 8.39518 10.2299 8.11321C10.8389 7.37338 11.6505 6.87077 12.6054 6.88544C13.9328 6.88904 15.1146 8.00249 15.1146 9.40106C15.1146 10.2538 14.5098 10.8916 13.9551 11.2668C13.3804 11.6555 12.6437 11.9167 12 11.9167H11.3394L13.7061 14.2834C13.9868 14.5641 13.9868 15.0192 13.7061 15.2999C13.4255 15.5806 12.9704 15.5806 12.6897 15.2999L9.60417 12.2144L6.51865 15.2999C6.23796 15.5806 5.78287 15.5806 5.50218 15.2999C5.22149 15.0192 5.22149 14.5641 5.50218 14.2834L7.86895 11.9167ZM10.5601 10.4792H12C12.3015 10.4792 12.7627 10.3379 13.1496 10.0761C13.5566 9.80085 13.6771 9.54019 13.6771 9.40106C13.6771 8.81814 13.1593 8.32293 12.599 8.32293H12.5862L12.5862 8.32282C12.1926 8.31584 11.7624 8.51343 11.3398 9.02679C11.0455 9.38437 10.7764 9.8733 10.5601 10.4792Z' fill='%233F8EC5'/%3E%3C/svg%3E");
    }
  }

  &__name {
    font-weight: 400;

    padding-right: 4px;
    padding-left: 8px;
  }

  &__value {
    font-weight: 600;

    &.is-first {
      padding-left: 8px;
    }
  }
}

.drinks-left {
  font-weight: 400;
  min-width: 115px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;

  font-size: 14px;
  letter-spacing: -0.4px;

  &__label {
    padding-right: 2px;
  }

  &__value {
    font-weight: 600;
    text-align: right;
  }
}
</style>
